import React, { FC } from "react";
import moment from "moment";
import useAccountTransactions from "../hooks/useAccountTransactions";
import { formatAsMoney } from "../helpers/numbers";
import { getTransactionIcon } from "../helpers/transaction";
import { Link } from "react-router-dom";
import type { NormalizedTransaction } from "../hooks/useAccountTransactions";

function groupByMonth(transactions: NormalizedTransaction[], max: number) {
  return transactions
    .slice(0, max)
    .reduce<Record<string, NormalizedTransaction[]>>((groups, tx) => {
      const key = moment(tx.date).format("YYYY-MM");
      if (!groups[key]) groups[key] = [];
      groups[key].push(tx);
      return groups;
    }, {});
}

const TransactionRow: FC<{
  tx: NormalizedTransaction;
  showLink: boolean;
}> = ({ tx, showLink }) => {
  const icon = getTransactionIcon(tx.type);
  const content = (
		<div className="flex items-center gap-2 p-2 hover:bg-visaro-gray-700/5 rounded-md cursor-pointer">
			<div
				className="p-1.5 lg:p-2 rounded-full"
				style={{ backgroundColor: icon.color + "20" }}
			>
				<icon.icon size={24} color={icon.color} />
			</div>
			<div className="flex-1 min-w-0">
				<div className="font-medium text-sm lg:text-base lg:truncate">
					{tx.description}
				</div>
				<div className="text-gray-500 text-xs lg:text-sm">
					{moment(tx.date).format("lll")}
				</div>
			</div>
			<div className="text-right shrink-0">
				<div className="font-medium text-base lg:text-lg">
					₦ {formatAsMoney(Math.abs(tx.amount))}
				</div>
				<div
					className={`p-1 rounded-md text-sm px-2 capitalize ${
						tx.status === "failed"
							? "bg-red-100 text-red-500"
							: tx.status === "successful" ||
								  tx.status === "credit"
								? "bg-green-100 text-green-500"
								: "bg-amber-100 text-amber-500"
					}`}
				>
					{tx.status}
				</div>
			</div>
		</div>
  );

  return showLink ? (
    <Link to={`/payments/history/tx/${tx.id}`} state={{ transaction: tx }}>
      {content}
    </Link>
  ) : (
    <div>{content}</div>
  );
};

const TransactionHistoryList: FC<{ max?: number }> = ({ max = Infinity }) => {
  const { transactions, isLoadingTransactions } = useAccountTransactions();
  const grouped = groupByMonth(transactions, max);

  if (isLoadingTransactions) {
    return (
      <div className="text-gray-500 animate-pulse">Loading transactions...</div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-lg text-gray-500">
        See when money comes in, and when it goes out. You&apos;ll find your
        recent Visaro activity here.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {Object.entries(grouped).map(([month, txs]) => (
        <div key={month} className="flex flex-col gap-2">
          <div className="font-medium text-gray-500">
            {moment(month).format("MMM")}
          </div>
          {txs.map((tx) => (
            <TransactionRow
              key={tx.id}
              tx={tx}
              showLink={!!tx.id}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default TransactionHistoryList;
